<template>
  <section :class="['chat-panel', `chat-panel--${backgroundState}`]">
    <div v-if="!selectedDialog" class="chat-panel__empty">
      <div class="chat-panel__empty-card">
        <img src="/img/chat.svg" alt="" class="chat-panel__empty-image" />
        <div class="chat-panel__empty-title">Диалог не выбран</div>
        <p>Выберите или создайте диалог для начала общения</p>
      </div>
    </div>

    <template v-else>
      <header class="chat-panel__header">
        <div class="chat-panel__header-meta">
          <div class="chat-panel__title">{{ selectedDialog.client_name || 'Диалог' }}</div>
          <div class="chat-panel__subtitle">{{ headerStatusText }}</div>
        </div>

        <div class="chat-panel__header-actions">
          <a
            v-if="selectedDialog.client_phone"
            :href="`tel:${selectedDialog.client_phone}`"
            class="chat-panel__header-icon chat-panel__header-icon--phone"
            aria-label="Позвонить клиенту"
          >
            <img src="/img/dialogs/header-phone-figma.svg" alt="" class="chat-panel__header-icon-image" />
          </a>
          <button
            v-else
            type="button"
            class="chat-panel__header-icon chat-panel__header-icon--phone is-muted"
            aria-label="Позвонить клиенту"
            disabled
          >
            <img src="/img/dialogs/header-phone-figma.svg" alt="" class="chat-panel__header-icon-image" />
          </button>

          <button
            type="button"
            class="chat-panel__header-action"
            :class="{ 'chat-panel__header-action--filled': canBookClient }"
            :aria-label="canBookClient ? 'Записать клиента' : 'Запись недоступна'"
            :disabled="!canBookClient"
            @click="openBooking"
          >
            <img src="/img/dialogs/chat-booking-figma.png" alt="" class="chat-panel__header-action-image" />
          </button>
        </div>
      </header>

      <div class="chat-panel__messages message-block" @scroll.passive="handleScroll" ref="chatContainer">
        <div v-for="(group, index) in groupedMessages" :key="index" class="chat-group">
          <div v-if="group.type === 'date'" class="chat-date-chip">{{ group.date }}</div>

          <div
            v-else
            class="chat-message-row"
            :class="group.isMine ? 'chat-message-row--mine' : 'chat-message-row--other'"
          >
            <div
              class="chat-message-stack"
              :class="group.isMine ? 'chat-message-stack--mine' : 'chat-message-stack--other'"
            >
              <div
                v-for="message in group.messages"
                :key="message.id"
                :data-message-id="message.id"
                :class="[
                  'chat-message-item',
                  { 'chat-message-item--voice': isVoiceMessage(message) },
                  { 'chat-message-item--call': isCallMessage(message) }
                ]"
              >
                <template v-if="isCallMessage(message)">
                  <div class="chat-call-card">
                    <img :src="callDirectionIcon(message)" alt="" class="chat-call-card__arrow" />
                    <img src="/img/dialogs/header-phone.png" alt="" class="chat-call-card__phone" />
                    <div class="chat-call-card__meta">
                      <div class="chat-call-card__primary">{{ callPrimaryText(message) }}</div>
                      <div v-if="callSecondaryText(message)" class="chat-call-card__secondary">
                        {{ callSecondaryText(message) }}
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else-if="isVoiceMessage(message)">
                  <div class="chat-voice-card">
                    <button type="button" class="chat-voice-card__play" aria-label="Воспроизвести голосовое сообщение">
                      <span class="chat-voice-card__triangle"></span>
                    </button>
                    <div
                      class="chat-voice-card__wave"
                      :style="{ backgroundImage: `url(${voiceWaveSrc})` }"
                      aria-hidden="true"
                    ></div>
                    <div class="chat-voice-card__duration">{{ voiceDurationText(message) }}</div>
                  </div>
                </template>

                <template v-else-if="shouldShowContent(message)">
                  <div
                    v-if="isHtmlAllowed()"
                    v-html="message.content"
                    class="chat-message-text"
                  ></div>
                  <div v-else class="chat-message-text">{{ message.content }}</div>
                </template>

                <div v-if="fileUrl(message) && !isVoiceMessage(message) && !isCallMessage(message)" class="chat-attachments">
                  <template v-if="isImageUrl(fileUrl(message))">
                    <el-image
                      :src="fileUrl(message)"
                      :preview-src-list="[fileUrl(message)]"
                      fit="cover"
                      class="chat-attachment-image"
                    />
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>Скачать</span>
                    </a>
                  </template>

                  <template v-else-if="isVideoUrl(fileUrl(message), message)">
                    <video :src="fileUrl(message)" controls class="chat-attachment-video" />
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>Скачать</span>
                    </a>
                  </template>

                  <template v-else-if="isAudioUrl(fileUrl(message), message)">
                    <audio :src="fileUrl(message)" controls class="chat-attachment-audio" />
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>Скачать</span>
                    </a>
                  </template>

                  <template v-else>
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>{{ attachmentName(message) }}</span>
                    </a>
                  </template>
                </div>

                <div class="chat-message-meta">{{ formatBubbleTime(message.created_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="chat-panel__composer">
        <el-upload
          ref="uploadRef"
          class="chat-panel__upload"
          :auto-upload="false"
          :action="uploadUrl"
          :data="{ client_id: selectedDialog?.client_id }"
          :show-file-list="false"
          :on-remove="handleUploadRemove"
          :before-upload="beforeUpload"
          :on-change="handleFileChange"
          :headers="{ Authorization: 'Bearer ' + getToken() }"
          v-model:file-list="uploadFiles"
        />

        <div v-if="uploadFiles.length" class="chat-panel__files">
          <div v-for="file in uploadFiles" :key="file.uid" class="chat-panel__file">
            <span class="chat-panel__file-name">{{ file.name }}</span>
            <el-progress
              v-if="typeof file.percentage === 'number'"
              :percentage="Math.round(file.percentage)"
              :stroke-width="6"
              :status="file.status === 'success' ? 'success' : (file.status === 'fail' ? 'exception' : undefined)"
              class="chat-panel__file-progress"
            />
            <span v-else class="chat-panel__file-ready">Готов к отправке</span>
            <button type="button" class="chat-panel__file-remove" @click="removeFile(file)">
              <el-icon><Close /></el-icon>
            </button>
          </div>
        </div>

        <div class="chat-panel__composer-shell">
          <button type="button" class="chat-panel__icon-btn chat-panel__icon-btn--attach" @click="triggerFileSelect">
            <img src="/img/dialogs/attach.png" alt="" class="chat-panel__composer-icon-image" />
          </button>

          <QuillEditor
            ref="editor"
            v-model:content="messageHtml"
            contentType="html"
            theme="snow"
            :toolbar="[]"
            placeholder="Сообщение..."
            class="chat-editor"
            @keydown.enter.exact.prevent="sendMessage"
          />

          <button
            type="button"
            class="chat-panel__icon-btn chat-panel__icon-btn--primary"
            :class="{ 'chat-panel__icon-btn--send': hasDraftContent }"
            @click="handlePrimaryAction"
            :disabled="isSending"
            :aria-label="hasDraftContent ? 'Отправить сообщение' : 'Голосовое сообщение'"
          >
            <span v-if="hasDraftContent" class="chat-panel__send-icon" aria-hidden="true"></span>
            <img
              v-else
              src="/img/dialogs/mic.png"
              alt=""
              class="chat-panel__composer-icon-image chat-panel__composer-icon-image--mic"
            />
          </button>
        </div>
      </footer>
    </template>
  </section>
</template>

<script>
import axios from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { mapState } from 'vuex'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { Close, Download } from '@element-plus/icons-vue'

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: 'ChatPanel',
  components: {
    Close,
    Download,
    QuillEditor
  },
  props: {
    selectedDialog: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loadingOlder: false,
      messageHtml: '',
      page: 1,
      pageSize: 25,
      seenFlushTimeout: null,
      observerInitialized: false,
      uploadUrl: this.$store.getters['messengers/getUploadUrl'],
      uploadFiles: [],
      pendingFiles: [],
      isSending: false,
      sendFileUrl: 'https://lk.lineon.me/api/messages.sendFile',
      voiceWaveSrc: '/img/dialogs/voice-wave.png'
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('messengers', ['messages', 'messengers']),
    ...mapState('socket', ['waAuthCheckInProgress', 'whatsappReadyStatuses']),
    currentMessenger() {
      return this.messengers.find(messenger => Number(messenger.id) === Number(this.selectedDialog?.messenger_id)) || null
    },
    hasLineonClient() {
      return Boolean(this.selectedDialog?.client_id || this.selectedDialog?.client_external_id)
    },
    canBookClient() {
      return Boolean(this.selectedDialog?.client_id)
    },
    hasDraftContent() {
      return this.hasMeaningfulHtml(this.messageHtml) || this.pendingFiles.length > 0
    },
    headerStatusText() {
      const rawDate = this.selectedDialog?.last_active
      if (!rawDate) return 'был(а) недавно'

      const timezoneName = this.user?.timezone || 'Europe/Moscow'
      const parsed = dayjs(String(rawDate).replace(' ', 'T')).tz(timezoneName)
      if (!parsed.isValid()) return 'был(а) недавно'

      const now = dayjs().tz(timezoneName)
      const minutesDiff = Math.abs(now.diff(parsed, 'minute'))
      if (minutesDiff <= 30) return 'был(а) недавно'
      if (parsed.isSame(now, 'day')) return `в сети ${parsed.format('HH:mm')}`
      if (parsed.isSame(now.subtract(1, 'day'), 'day')) return 'был(а) вчера'
      return `был(а) ${parsed.format('DD.MM.YYYY')}`
    },
    backgroundState() {
      const dialog = this.selectedDialog || {}
      const raw = [
        dialog.chat_background_state,
        dialog.background_state,
        dialog.ui_state,
        dialog.dialog_state,
        dialog.booking_state,
        dialog.appointment_state,
        dialog.appointment_status,
        dialog.record_status,
        dialog.visit_status,
        dialog.status,
        dialog.active_appointment,
        dialog.current_appointment,
        dialog.upcoming_appointment,
        dialog.next_appointment,
        dialog.last_appointment
      ]
        .filter(Boolean)
        .map(value => typeof value === 'object' ? JSON.stringify(value) : String(value))
        .join(' ')
        .toLowerCase()

      const hasOverdueBooking = Boolean(
        dialog.is_overdue ||
        dialog.is_expired ||
        dialog.has_overdue_appointment ||
        dialog.has_expired_appointment ||
        dialog.overdue_appointment ||
        dialog.expired_appointment ||
        dialog.missed_appointment
      )

      const hasCurrentBooking = Boolean(
        dialog.has_active_appointment ||
        dialog.has_current_appointment ||
        dialog.has_upcoming_appointment ||
        dialog.has_active_booking ||
        dialog.active_appointment ||
        dialog.current_appointment ||
        dialog.upcoming_appointment ||
        dialog.next_appointment ||
        dialog.active_record ||
        dialog.current_record ||
        dialog.upcoming_record ||
        dialog.next_record
      )

      if (
        hasOverdueBooking ||
        /(overdue|expired|red|late|missed|no[_ -]?show|canceled|cancelled|просроч|неявк)/.test(raw)
      ) return 'overdue'

      if (
        hasCurrentBooking ||
        /(success|green|confirmed|paid|completed|scheduled|planned|upcoming|active[_ -]?(appointment|booking|record|visit))/.test(raw)
      ) return 'success'

      return 'default'
    },
    canSendMessage() {
      return !this.isSending && this.hasDraftContent
    },
    sortedMessages() {
      const dialogId = this.selectedDialog?.id
      if (!dialogId) return []
      return this.messages
        .filter(message => String(message.dialog_id) === String(dialogId))
        .map(message => ({
          ...message,
          id: Number(message.id),
          dialog_id: Number(message.dialog_id),
          sender_user_id: message.sender_user_id ? Number(message.sender_user_id) : null,
          created_at: new Date(Number(message.created_at) * 1000),
          updated_at: message.updated_at ? new Date(Number(message.updated_at) * 1000) : null,
          attachments: message.attachments
            ? (typeof message.attachments === 'string'
              ? (() => {
                try {
                  return JSON.parse(message.attachments)
                } catch {
                  return null
                }
              })()
              : message.attachments)
            : null,
          sender_name: message.sender_name || ''
        }))
        .sort((first, second) => first.created_at - second.created_at)
    },
    groupedMessages() {
      const groups = []
      let currentGroup = null
      let lastDate = null

      for (const message of this.sortedMessages) {
        const messageDate = dayjs(message.created_at).format('DD.MM.YYYY')

        if (messageDate !== lastDate) {
          groups.push({ type: 'date', date: messageDate })
          lastDate = messageDate
        }

        const timestamp = message.created_at.getTime()
        if (
          currentGroup &&
          currentGroup.type === 'messages' &&
          currentGroup.sender_type === message.sender_type &&
          currentGroup.sender_user_id === message.sender_user_id &&
          Math.abs(timestamp - currentGroup.lastTimestamp) <= 60000
        ) {
          currentGroup.messages.push(message)
          currentGroup.lastTimestamp = timestamp
        } else {
          if (currentGroup) groups.push(currentGroup)
          currentGroup = {
            type: 'messages',
            sender_type: message.sender_type,
            sender_user_id: message.sender_user_id,
            isMine: message.sender_type === 'manager',
            messages: [message],
            lastTimestamp: timestamp
          }
        }
      }

      if (currentGroup) groups.push(currentGroup)
      return groups
    }
  },
  watch: {
    selectedDialog: {
      immediate: true,
      handler(newVal, oldVal) {
        try {
          const token = this.$cookies.get('token') || (this.$store.state && this.$store.state.cookies && this.$store.state.cookies.get && this.$store.state.cookies.get('token'))
          if (token) {
            const decodeJwt = (typeof window !== 'undefined' && window.decodeJwt) ? window.decodeJwt : (require && require('jwt-decode'))
            const decoded = typeof decodeJwt === 'function' ? decodeJwt(token) : decodeJwt.default(token)
            const accountId = decoded.account_id
            if (oldVal && oldVal.id) {
              const oldChannel = `new-message_${accountId}_${oldVal.id}`
              if (this.$store.state.socket && this.$store.state.socket.socket && this.$store.state.socket.socket.off) {
                this.$store.state.socket.socket.off(oldChannel)
              }
            }
          }
        } catch (error) {
          console.error(error)
        }

        this.page = 1
        this.observerInitialized = false
        this.pendingFiles = []
        this.uploadFiles = []

        if (!newVal) {
          this.messageHtml = ''
        }

        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },
    messages() {
      if (!this.loadingOlder) {
        this.scrollToBottom()
      }
    },
    sortedMessages(newValue) {
      if (newValue.length && this.observer) {
        this.observer.disconnect()
      }

      if (newValue.length && !this.observerInitialized) {
        this.observerInitialized = true
        this.$nextTick(() => {
          const container = this.$refs.chatContainer
          if (!container || !this.observer) return
          const allElements = container.querySelectorAll('[data-message-id]')
          allElements.forEach(element => {
            this.observer.observe(element)
          })
        })
      }
    }
  },
  methods: {
    openBooking() {
      if (!this.canBookClient) {
        this.$message.warning('Клиент не привязан к LineOn')
        return
      }

      this.$router.push({
        path: '/',
        query: {
          booking: '1',
          client_id: String(this.selectedDialog.client_id),
          client_name: this.selectedDialog.client_name || '',
          client_phone: this.selectedDialog.client_phone || ''
        }
      }).then(() => {
        this.$message.info('Выберите свободное время для записи клиента')
      })
    },
    handlePrimaryAction() {
      if (this.canSendMessage) {
        this.sendMessage()
      }
    },
    triggerFileSelect() {
      const upload = this.$refs.uploadRef
      if (upload && upload.$el) {
        const input = upload.$el.querySelector('input[type=file]')
        if (input) input.click()
      }
    },
    getToken() {
      return document.cookie.split('; ').find(row => row.startsWith('token='))?.split('=')[1] || ''
    },
    handleUploadRemove(file, fileList) {
      this.uploadFiles = fileList
      this.pendingFiles = fileList.map(item => item)
    },
    removeFile(file) {
      this.uploadFiles = this.uploadFiles.filter(item => item.uid !== file.uid)
      this.pendingFiles = this.pendingFiles.filter(item => item.uid !== file.uid)
    },
    formatDate(date) {
      if (!date) return ''
      const accountTimezone = this.user.timezone || 'Europe/Moscow'
      const local = dayjs(date).tz(accountTimezone)
      const now = dayjs().tz(accountTimezone)
      const yesterday = now.subtract(1, 'day')

      if (local.isSame(now, 'day')) {
        return `Сегодня ${local.format('HH:mm')}`
      }
      if (local.isSame(yesterday, 'day')) {
        return `Вчера ${local.format('HH:mm')}`
      }
      return local.isValid() ? local.format('DD.MM.YYYY HH:mm') : ''
    },
    formatBubbleTime(date) {
      if (!date) return ''
      const accountTimezone = this.user.timezone || 'Europe/Moscow'
      const local = dayjs(date).tz(accountTimezone)
      return local.isValid() ? local.format('HH:mm') : ''
    },
    messageTypeValue(message) {
      return [
        message?.message_type,
        message?.type,
        message?.event_type,
        message?.kind,
        message?.subtype,
        message?.call_type,
        message?.call_status
      ]
        .filter(Boolean)
        .map(value => String(value).toLowerCase())
        .join(' ')
    },
    isVoiceMessage(message) {
      const typeValue = this.messageTypeValue(message)
      if (/(voice|audio|ptt|voice_note|audio_note)/.test(typeValue)) return true
      return this.isAudioUrl(this.fileUrl(message), message)
    },
    isCallMessage(message) {
      const typeValue = this.messageTypeValue(message)
      if (/(call|missed|declined|rejected|incoming|outgoing)/.test(typeValue)) return true
      return Boolean(message?.call_status || message?.call_duration || message?.call_direction)
    },
    voiceDurationText(message) {
      const raw =
        message?.voice_duration ||
        message?.duration ||
        message?.audio_duration ||
        message?.meta?.duration

      if (raw == null || raw === '') return this.formatBubbleTime(message.created_at)

      const seconds = Number(raw)
      if (Number.isNaN(seconds)) return String(raw)
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    },
    callDirectionIcon(message) {
      const typeValue = this.messageTypeValue(message)
      if (/(missed|declined|rejected|failed|cancelled|canceled)/.test(typeValue)) {
        return '/img/dialogs/call-arrow-down-red.png'
      }
      if (/(outgoing|dialed|dialled)/.test(typeValue)) {
        return '/img/dialogs/call-arrow-down-blue.png'
      }
      return '/img/dialogs/call-arrow-up.png'
    },
    callPrimaryText(message) {
      const duration = message?.call_duration || message?.duration || message?.meta?.duration
      if (duration != null && duration !== '') {
        const seconds = Number(duration)
        if (!Number.isNaN(seconds)) {
          const mins = Math.floor(seconds / 60)
          const secs = seconds % 60
          return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
        }
        return String(duration)
      }
      if (message?.content) return String(message.content).replace(/<[^>]+>/g, '').trim() || 'Звонок'
      return 'Звонок'
    },
    callSecondaryText(message) {
      if (message?.call_status_text) return String(message.call_status_text)
      return this.formatBubbleTime(message.created_at)
    },
    hasMeaningfulHtml(html) {
      return String(html || '')
        .replace(/<p><br><\/p>/gi, '')
        .replace(/<p>\s*<\/p>/gi, '')
        .replace(/<br\s*\/?>(?=\s*$)/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim().length > 0
    },
    async handleScroll() {
      const container = this.$refs.chatContainer
      if (!container || container.scrollTop !== 0 || this.loadingOlder) return

      const previousHeight = container.scrollHeight
      this.loadingOlder = true
      this.page += 1

      await this.$store.dispatch('messengers/fetchMessages', {
        dialog_id: this.selectedDialog.id,
        page: this.page,
        pageSize: this.pageSize
      })

      this.loadingOlder = false
      this.$nextTick(() => {
        const newHeight = container.scrollHeight
        container.scrollTop = newHeight - previousHeight
      })
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer
        if (!container) return
        container.scrollTop = container.scrollHeight
      })
    },
    isHtmlAllowed() {
      return ['email', 'telegram', 'whatsapp'].includes(this.currentMessenger?.type)
    },
    async sendMessage() {
      const html = String(this.messageHtml || '').trim()
      if (!this.hasMeaningfulHtml(html) && this.pendingFiles.length === 0) return

      const messenger = this.currentMessenger
      if (!messenger) return

      if (messenger.type === 'whatsapp') {
        this.isSending = true
        try {
          if (this.pendingFiles.length > 0) {
            const rawHtml = html
            for (let index = 0; index < this.pendingFiles.length; index += 1) {
              const file = this.pendingFiles[index]
              const formData = new FormData()
              formData.append('dialog_id', Number(this.selectedDialog.id))
              formData.append('files[]', file.raw)
              formData.append('caption', index === 0 ? rawHtml : '')
              formData.append('content', rawHtml)
              formData.append('content_raw', rawHtml)
              formData.append('messenger_id', this.selectedDialog.messenger_id)
              formData.append('client_id', Number(this.selectedDialog.client_phone))
              formData.append('client_phone', this.selectedDialog.client_phone)
              formData.append('messenger_type', messenger.type)

              file.status = 'uploading'
              file.percentage = 0
              try {
                await axios.post(this.sendFileUrl, formData, {
                  headers: { Authorization: 'Bearer ' + this.getToken() },
                  onUploadProgress: event => {
                    if (event.total) file.percentage = Math.round((event.loaded / event.total) * 100)
                  }
                })
                file.status = 'success'
              } catch (error) {
                console.error('sendFile error', error)
                file.status = 'fail'
              }
              await new Promise(resolve => setTimeout(resolve, 300))
            }

            this.pendingFiles = []
            this.uploadFiles = []
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          } else {
            this.$store.dispatch('socket/send', {
              event: 'send-whatsapp-message',
              data: {
                messenger_id: messenger.id,
                client_id: Number(this.selectedDialog.client_phone),
                dialog_id: Number(this.selectedDialog.id),
                content: html
              }
            })
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          }
        } finally {
          this.isSending = false
        }
      } else if (messenger.type === 'email') {
        this.isSending = true
        try {
          const formData = new FormData()
          formData.append('dialog_id', Number(this.selectedDialog.id))
          formData.append('text', html)
          for (const file of this.pendingFiles) {
            formData.append('files[]', file.raw)
          }

          await axios.post('https://lk.lineon.me/api/email.send', formData, {
            headers: { Authorization: 'Bearer ' + this.getToken() }
          })

          this.pendingFiles = []
          this.uploadFiles = []
          this.messageHtml = ''
          if (this.$refs.editor && this.$refs.editor.setHTML) {
            this.$refs.editor.setHTML('')
          }
          this.scrollToBottom()
        } finally {
          this.isSending = false
        }
      } else if (messenger.type === 'telegram') {
        this.isSending = true
        try {
          const text = html
          if (!this.hasMeaningfulHtml(text) && this.pendingFiles.length === 0) return

          if (this.pendingFiles.length > 0) {
            for (let index = 0; index < this.pendingFiles.length; index += 1) {
              const file = this.pendingFiles[index]
              const formData = new FormData()
              formData.append('dialog_id', Number(this.selectedDialog.id))
              formData.append('files[]', file.raw)
              formData.append('messenger_id', this.selectedDialog.messenger_id)
              formData.append('client_id', Number(this.selectedDialog.client_id))
              formData.append('caption', index === 0 ? text : '')
              formData.append('messenger_type', messenger.type)

              file.status = 'uploading'
              file.percentage = 0
              try {
                await axios.post(this.sendFileUrl, formData, {
                  headers: { Authorization: 'Bearer ' + this.getToken() },
                  onUploadProgress: event => {
                    if (event.total) file.percentage = Math.round((event.loaded / event.total) * 100)
                  }
                })
                file.status = 'success'
              } catch (error) {
                console.error('sendFile error', error)
                file.status = 'fail'
              }
              await new Promise(resolve => setTimeout(resolve, 300))
            }
            this.pendingFiles = []
            this.uploadFiles = []
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          } else {
            this.$store.dispatch('socket/send', {
              event: 'send-telegram-message',
              data: {
                messenger_id: messenger.id,
                dialog_id: Number(this.selectedDialog.id),
                client_id: Number(this.selectedDialog.client_id),
                content: text
              }
            })
            this.messageHtml = ''
            this.uploadFiles = []
            this.pendingFiles = []
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          }
        } finally {
          this.isSending = false
        }
      }
    },
    beforeUpload() {
      if (!this.selectedDialog || !this.selectedDialog.client_id) {
        this.$message.warning('Выберите диалог перед загрузкой файла')
        return false
      }
      return false
    },
    handleFileChange(file, fileList) {
      this.uploadFiles = fileList
      this.pendingFiles = fileList.map(item => item)
    },
    fileUrl(message) {
      if (message && typeof message.file_url === 'string' && message.file_url) return message.file_url
      const attachments = message && message.attachments
      if (!attachments) return ''
      if (typeof attachments === 'string') return attachments
      if (Array.isArray(attachments) && attachments.length) {
        const firstAttachment = attachments[0]
        if (typeof firstAttachment === 'string') return firstAttachment
        if (firstAttachment && typeof firstAttachment === 'object' && firstAttachment.url) return firstAttachment.url
      }
      if (typeof attachments === 'object' && attachments.url) return attachments.url
      return ''
    },
    isImageUrl(url) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url || '')
    },
    isVideoUrl(url, message) {
      const mime = (message?.mimetype || '').toLowerCase()
      if (mime) return mime.startsWith('video/')
      const extension = (url || '').toLowerCase()
      return extension.endsWith('.mp4') || extension.endsWith('.webm')
    },
    isAudioUrl(url, message) {
      const mime = (message?.mimetype || '').toLowerCase()
      if (mime) return mime.startsWith('audio/')
      const extension = (url || '').toLowerCase()
      return /\.(mp3|m4a|aac|ogg|oga|wav|opus)$/i.test(extension)
    },
    attachmentName(message) {
      const url = this.fileUrl(message) || ''
      try {
        const lastChunk = url.split('/').pop() || ''
        const decoded = decodeURIComponent(lastChunk)
        return decoded || 'файл'
      } catch {
        return 'файл'
      }
    },
    shouldShowContent(message) {
      const text = (message && message.content ? String(message.content) : '').trim()
      if (!text) return false
      const url = this.fileUrl(message)
      if (!url) return true
      try {
        const name = this.attachmentName(message).trim()
        return text !== name
      } catch {
        return true
      }
    }
  },
  mounted() {
    this.seenMessages = new Set()
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        const messageId = entry.target.getAttribute('data-message-id')
        const message = this.sortedMessages.find(item => String(item.id) === String(messageId))
        if (message && !message.read_at && message.sender_type !== 'manager') {
          this.seenMessages.add(message.id)
          if (this.seenFlushTimeout) {
            clearTimeout(this.seenFlushTimeout)
          }
          this.seenFlushTimeout = setTimeout(() => {
            const ids = Array.from(this.seenMessages)
            if (ids.length) {
              this.seenMessages.clear()
              this.$store.dispatch('messengers/markAsReadBatch', {
                message_ids: ids,
                dialog_id: Number(this.selectedDialog.id),
                messenger_type: this.selectedDialog.messenger_type
              })
            }
            this.seenFlushTimeout = null
          }, 500)
        }
      })
    }, { threshold: 0.5 })

    this.$nextTick(() => {
      setTimeout(() => {
        const container = this.$refs.chatContainer
        if (!container || !this.observer) return
        const allElements = container.querySelectorAll('[data-message-id]')
        allElements.forEach(element => {
          this.observer.observe(element)
        })
        this.scrollToBottom()
      }, 50)
    })
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>

<style scoped>
.chat-panel {
  --chat-border: rgba(49, 90, 157, 0.95);
  --chat-border-soft: rgba(101, 119, 160, 0.52);
  --chat-deep: #06122c;
  --chat-deep-soft: #0a1838;
  --chat-cyan: #22c0ff;
  --chat-cyan-dim: #02a9de;
  --chat-white: #ffffff;
  --chat-header-bg: #00183b;
  --chat-composer-bg: #17386f url("../../../../public/img/dialogs/chat-composer.png") center / 100% 100% no-repeat;
  --chat-surface-bg: #031230;
  --chat-incoming-bg: linear-gradient(180deg, #4f82d5 0%, #3b68b9 100%);
  --chat-outgoing-bg: linear-gradient(180deg, #4572c4 0%, #315fae 100%);
  --chat-call-bg: linear-gradient(180deg, #4b7cce 0%, #3968bc 100%);
  --chat-wallpaper: url("../../../../public/img/dialogs/chat-wallpaper-figma.svg");
  --chat-overlay: rgba(0, 61, 145, 0.18);
  min-height: 0;
  height: 100%;
  max-height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
}

.chat-panel__empty {
  min-height: 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: var(--chat-header-bg);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__empty-card {
  width: min(420px, calc(100% - 32px));
  padding: 28px 24px;
  text-align: center;
  border-radius: 16px;
  background: rgba(8, 22, 48, 0.76);
  box-shadow: inset 0 0 0 1px rgba(66, 110, 187, 0.32);
}

.chat-panel__empty-image {
  width: 92px;
  height: 92px;
  object-fit: contain;
  opacity: 0.45;
}

.chat-panel__empty-title {
  margin-top: 14px;
  color: #ffffff;
  font-family: var(--ui-font-family-base);
  font-size: 30px;
}

.chat-panel__empty-card p {
  margin: 10px 0 0;
  color: #9ec4ff;
  font-family: var(--ui-font-family-base);
  font-size: 18px;
}

.chat-panel__header {
  box-sizing: border-box;
  height: clamp(54px, 4.3vw, 62px);
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 16px;
  border-radius: 11px;
  background: var(--chat-header-bg);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.51);
  overflow: hidden;
}

.chat-panel__header-meta {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

.chat-panel__header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.chat-panel__header-icon,
.chat-panel__header-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
  color: #22c0ff;
  text-decoration: none;
  outline: none;
  box-shadow: none;
}

.chat-panel__header-icon {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: rgba(0, 24, 59, 0.92);
  box-shadow: inset 0 0 0 1px rgba(0, 35, 89, 1);
}

.chat-panel__header-icon.is-muted {
  opacity: 0.45;
  cursor: default;
}

.chat-panel__header-action {
  width: 46px;
  height: 46px;
  border-radius: 11px;
  background: rgba(0, 24, 59, 0.92);
  box-shadow: inset 0 0 0 1px rgba(0, 35, 89, 1);
}

.chat-panel__header-action:disabled {
  opacity: 0.45;
  cursor: default;
}

.chat-panel__header-action--filled {
  background: linear-gradient(90deg, #44b4e2 0%, #22c0ff 100%);
  box-shadow: none;
}

.chat-panel__header-action--filled .chat-panel__header-action-image {
  opacity: 1;
}

.chat-panel__header-icon-image {
  width: 34px;
  height: 34px;
  display: block;
  object-fit: contain;
}

.chat-panel__header-action-image {
  width: 18px;
  height: 18px;
  display: block;
  object-fit: contain;
}

.chat-panel__title {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--chat-white);
  font-family: var(--ui-font-family-base);
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}

.chat-panel__subtitle {
  margin-top: 3px;
  color: #c9c9c9;
  font-family: var(--ui-font-family-base);
  font-size: 14px;
  font-weight: 300;
  line-height: 1;
}

.chat-panel__messages {
  position: relative;
  isolation: isolate;
  min-height: 0;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 14px 18px 12px;
  border-radius: 10px;
  background: var(--chat-surface-bg);
  box-shadow: none;
  scrollbar-width: thin;
  scrollbar-color: rgba(160, 193, 255, 0.24) transparent;
}

.chat-panel__messages::before,
.chat-panel__messages::after {
  content: "";
  position: absolute;
  pointer-events: none;
}

.chat-panel__messages::before {
  inset: 0;
  z-index: 0;
  border-radius: 8px;
  background: var(--chat-wallpaper) center / cover no-repeat;
}

.chat-panel__messages::after {
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  background: var(--chat-overlay);
}

.chat-panel__messages > * {
  position: relative;
  z-index: 1;
}

.chat-panel__messages::-webkit-scrollbar {
  width: 10px;
}

.chat-panel__messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-panel__messages::-webkit-scrollbar-thumb {
  border: 3px solid transparent;
  border-radius: 999px;
  background: rgba(160, 193, 255, 0.24);
  background-clip: padding-box;
}

.chat-panel--success .chat-panel__messages {
  --chat-overlay: rgba(15, 112, 66, 0.48);
}

.chat-panel--overdue .chat-panel__messages {
  --chat-overlay: rgba(132, 26, 45, 0.48);
}

.chat-group + .chat-group {
  margin-top: 10px;
}

.chat-date-chip {
  width: fit-content;
  margin: 0 auto 16px;
  padding: 3px 12px;
  border-radius: 999px;
  background: rgba(16, 41, 85, 0.74);
  color: #9dc8ff;
  font-family: var(--ui-font-family-base);
  font-size: 11px;
  font-weight: 300;
}

.chat-message-row {
  display: flex;
  margin-bottom: 12px;
}

.chat-message-row--mine {
  justify-content: flex-end;
}

.chat-message-row--other {
  justify-content: flex-start;
}

.chat-message-stack {
  max-width: min(80%, 980px);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chat-message-stack--mine .chat-message-item {
  border-top-right-radius: 10px;
  background: var(--chat-outgoing-bg);
}

.chat-message-stack--other .chat-message-item {
  border-top-left-radius: 10px;
  background: var(--chat-incoming-bg);
}

.chat-message-item {
  position: relative;
  min-width: 122px;
  padding: 8px 12px 7px;
  border-radius: 12px;
  box-shadow: inset 0 0 0 1px rgba(112, 139, 191, 0.24);
}

.chat-message-text {
  color: #ffffff;
  font-family: var(--ui-font-family-base);
  font-size: 12px;
  font-weight: 300;
  line-height: 1.2;
  word-break: break-word;
}

.chat-message-meta {
  margin-top: 3px;
  text-align: right;
  color: #86a8df;
  font-family: var(--ui-font-family-base);
  font-size: 10px;
  font-weight: 300;
}

.chat-attachments {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chat-message-item--voice,
.chat-message-item--call {
  padding: 8px 11px 7px;
  min-width: 154px;
}

.chat-voice-card {
  display: grid;
  grid-template-columns: 26px minmax(118px, 1fr) auto;
  align-items: center;
  gap: 8px;
  min-height: 24px;
}

.chat-voice-card__play {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.96);
  padding: 0;
  box-shadow: 0 2px 8px rgba(9, 19, 42, 0.16);
}

.chat-voice-card__triangle {
  width: 0;
  height: 0;
  margin-left: 2px;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 8px solid #4f82d5;
}

.chat-voice-card__wave {
  height: 11px;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
}

.chat-voice-card__duration {
  color: #d8ebff;
  font-family: var(--ui-font-family-base);
  font-size: 11px;
  font-weight: 300;
  line-height: 1;
  white-space: nowrap;
}

.chat-call-card {
  position: relative;
  min-height: 32px;
  padding: 1px 28px 0 0;
}

.chat-call-card__arrow {
  width: 12px;
  height: 12px;
  position: absolute;
  left: 0;
  bottom: 2px;
  display: block;
  object-fit: contain;
}

.chat-call-card__phone {
  width: 18px;
  height: 18px;
  position: absolute;
  right: 0;
  top: 2px;
  display: block;
  object-fit: contain;
}

.chat-call-card__meta {
  min-width: 0;
  padding-left: 18px;
}

.chat-call-card__primary {
  color: #ffffff;
  font-family: var(--ui-font-family-base);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.05;
}

.chat-call-card__secondary {
  margin-top: 3px;
  color: #c7daf8;
  font-family: var(--ui-font-family-base);
  font-size: 10px;
  font-weight: 300;
  line-height: 1;
}

.chat-attachment-image,
.chat-attachment-video {
  width: min(220px, 100%);
  max-height: 220px;
  max-width: 100%;
  border-radius: 10px;
  overflow: hidden;
  object-fit: cover;
}

.chat-attachment-audio {
  width: min(240px, 100%);
  max-width: 100%;
}

.chat-attachment-link {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #c5e7ff;
  text-decoration: none;
  font-family: var(--ui-font-family-base);
  font-size: 14px;
}

.chat-panel__composer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-panel__upload {
  display: none;
}

.chat-panel__files {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-panel__file {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(160px, 220px) auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  background: linear-gradient(180deg, #17366b 0%, #112954 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__file-name,
.chat-panel__file-ready {
  min-width: 0;
  color: #d7e8ff;
  font-family: var(--ui-font-family-base);
  font-size: 15px;
}

.chat-panel__file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-panel__file-ready {
  color: #8de5a5;
}

.chat-panel__file-remove {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: #0c224b;
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
  color: #ffffff;
}

.chat-panel__composer-shell {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 10px 16px;
  border-radius: 20px;
  background: var(--chat-composer-bg);
  box-shadow: none;
  overflow: hidden;
}

.chat-panel__icon-btn {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #22c0ff;
  padding: 0;
  outline: none;
  box-shadow: none;
}

.chat-panel__composer-icon-image {
  display: block;
  object-fit: contain;
}

.chat-panel__icon-btn--attach .chat-panel__composer-icon-image {
  width: 24px;
  height: 30px;
  opacity: 0.9;
}

.chat-panel__icon-btn--primary .chat-panel__composer-icon-image {
  width: 18px;
  height: 34px;
}

.chat-panel__icon-btn--primary {
  background: transparent;
}

.chat-panel__icon-btn--send {
  color: #22c0ff;
}

.chat-panel__composer-icon-image--mic {
  width: 18px;
  height: 34px;
}

.chat-panel__send-icon {
  width: 0;
  height: 0;
  border-top: 11px solid transparent;
  border-bottom: 11px solid transparent;
  border-left: 22px solid #22c0ff;
  transform: translateX(2px);
}

.chat-panel__icon-btn:disabled {
  opacity: 0.4;
}

.chat-editor {
  min-width: 0;
}

.chat-editor :deep(.ql-toolbar) {
  display: none;
}

.chat-editor :deep(.ql-container) {
  border: none !important;
  font-family: var(--ui-font-family-base);
  background: transparent;
}

.chat-editor :deep(.ql-editor) {
  min-height: 44px;
  padding: 2px 0;
  color: #dbe8ff;
  font-size: 16px;
  line-height: 1.35;
}

.chat-editor :deep(.ql-editor.ql-blank::before) {
  left: 0;
  right: auto;
  color: #344c71;
  font-style: normal;
}

.chat-editor :deep(.ql-editor p) {
  margin: 0;
}

.chat-message-text :deep(p) {
  margin: 0;
}

.chat-message-text :deep(a) {
  color: #d8f1ff;
}

.chat-panel__file-progress :deep(.el-progress-bar__outer) {
  background: rgba(255, 255, 255, 0.12) !important;
}

.chat-panel__header-icon:hover,
.chat-panel__header-action:hover:not(:disabled),
.chat-panel__icon-btn:hover:not(:disabled) {
  box-shadow: inset 0 0 0 1px rgba(0, 195, 254, 0.82);
}

.chat-panel__header-action--filled:hover:not(:disabled) {
  box-shadow: none;
}

.chat-panel__header-icon:focus-visible,
.chat-panel__header-action:focus-visible,
.chat-panel__icon-btn:focus-visible {
  outline: none;
  box-shadow: none;
}

@media (max-width: 1180px) {
  .chat-panel__title {
    font-size: 18px;
  }

  .chat-message-stack {
    max-width: 84%;
  }
}

@media (max-width: 900px) {
  .chat-panel__header {
    padding: 8px 14px;
  }

  .chat-panel__title {
    font-size: 18px;
  }

  .chat-panel__messages {
    padding: 12px 14px 10px;
  }

  .chat-panel__file {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .chat-panel__header {
    height: 52px;
    min-height: 52px;
    gap: 10px;
  }

  .chat-panel__header-meta {
    align-items: flex-start;
  }

  .chat-panel__header-icon,
  .chat-panel__header-action {
    width: 40px;
    height: 40px;
  }

  .chat-panel__header-icon-image {
    width: 30px;
    height: 30px;
  }

  .chat-panel__header-action-image {
    width: 20px;
    height: 20px;
  }

  .chat-panel__title {
    font-size: 17px;
  }

  .chat-panel__subtitle {
    font-size: 12px;
  }

  .chat-message-stack {
    max-width: 94%;
  }

  .chat-message-item {
    min-width: 0;
  }

  .chat-message-text {
    font-size: 13px;
  }

  .chat-panel__composer-shell {
    gap: 10px;
    padding: 9px 14px;
  }

  .chat-voice-card {
    grid-template-columns: 28px minmax(88px, 1fr) auto;
    gap: 8px;
  }

  .chat-voice-card__wave {
    height: 12px;
  }

  .chat-call-card__primary {
    font-size: 12px;
  }

  .chat-call-card__secondary,
  .chat-voice-card__duration,
  .chat-message-meta {
    font-size: 10px;
  }
}

@media (max-width: 420px) {
  .chat-panel__messages {
    padding: 10px 12px 8px;
  }

  .chat-panel__header {
    padding: 8px 10px;
  }

  .chat-panel__composer-shell {
    padding: 8px 12px;
  }

  .chat-panel__title {
    font-size: 16px;
  }

  .chat-panel__subtitle {
    font-size: 10px;
  }

  .chat-panel__icon-btn {
    width: 38px;
    height: 38px;
  }

  .chat-panel__icon-btn--attach .chat-panel__composer-icon-image {
    width: 24px;
    height: 28px;
  }

  .chat-panel__icon-btn--primary .chat-panel__composer-icon-image {
    width: 18px;
    height: 30px;
  }

  .chat-message-stack {
    max-width: 100%;
  }
}
</style>

